import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Grid,
  IconButton
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import Navbar from '../components/Navbar';
import useTranslation from '../hooks/useTranslation';

const OperatorPaymentHistory = () => {
  const { t } = useTranslation();
  const [paymentReports, setPaymentReports] = useState([]);
  const [reportDetails, setReportDetails] = useState(null);
  const [reportDetailsDialog, setReportDetailsDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const history = useHistory();

  useEffect(() => {
    fetchPaymentReports();
  }, []);

  const fetchPaymentReports = async () => {
    try {
      const operatorId = localStorage.getItem('operatorId');
      const response = await axios.get(`/operators/${operatorId}/payment-reports`);
      setPaymentReports(response.data);
    } catch (error) {
      console.error('Error fetching payment reports:', error);
      toast.error(t('pages.tours.operator.dashboard.notifications.loadError.reports'));
    } finally {
      setLoading(false);
    }
  };

  const fetchReportDetails = async (date) => {
    try {
      const operatorId = localStorage.getItem('operatorId');
      const response = await axios.get(`/operators/${operatorId}/payment-reports/${date}`);
      setReportDetails(response.data);
      setReportDetailsDialog(true);
    } catch (error) {
      console.error('Error fetching report details:', error);
      toast.error(t('pages.tours.operator.dashboard.notifications.loadError.reportDetails'));
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7f9' }}>
      <Navbar hideFilters />
      <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push('/operator/dashboard')}
            sx={{ mr: 2 }}
          >
            {t('pages.tours.operator.dashboard.backToDashboard')}
          </Button>
          <Typography variant="h4" component="h1">
            {t('pages.tours.operator.dashboard.paymentHistory')}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : paymentReports.length > 0 ? (
          <TableContainer component={Paper} sx={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>{t('pages.tours.operator.dashboard.paymentTable.date')}</TableCell>
                  <TableCell>{t('pages.tours.operator.dashboard.paymentTable.transactions')}</TableCell>
                  <TableCell>{t('pages.tours.operator.dashboard.paymentTable.duePayment')} (DOP)</TableCell>
                  <TableCell>{t('pages.tours.operator.dashboard.paymentTable.status')}</TableCell>
                  <TableCell>{t('pages.tours.operator.dashboard.paymentTable.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentReports.map((report) => (
                  <TableRow 
                    key={report.date}
                    sx={{ '&:hover': { bgcolor: '#f8f8f8' } }}
                  >
                    <TableCell>{report.date}</TableCell>
                    <TableCell>{report.transactionCount}</TableCell>
                    <TableCell sx={{ color: '#2e7d32', fontWeight: 500 }}>
                      {formatAmount(report.finalAmount)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={report.status === 'processed' ? <CheckCircleIcon /> : <ScheduleIcon />}
                        label={report.status === 'processed' ? 
                          t('pages.tours.operator.dashboard.paymentTable.processed') : 
                          t('pages.tours.operator.dashboard.paymentTable.pending')}
                        color={report.status === 'processed' ? 'success' : 'warning'}
                        sx={{ minWidth: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => fetchReportDetails(report.date)}
                        sx={{ 
                          borderColor: '#316C6E',
                          color: '#316C6E',
                          '&:hover': {
                            borderColor: '#254F51',
                            bgcolor: 'rgba(49, 108, 110, 0.04)'
                          }
                        }}
                      >
                        {t('pages.tours.operator.dashboard.paymentTable.viewDetails')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <ReceiptIcon sx={{ fontSize: 40, color: '#666', mb: 1 }} />
            <Typography color="textSecondary">
              No payment reports available
            </Typography>
          </Box>
        )}

        {/* Report Details Dialog */}
        <Dialog
          open={reportDetailsDialog}
          onClose={() => setReportDetailsDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: '#f8f8f8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiptIcon sx={{ color: '#316C6E' }} />
              {t('pages.tours.operator.dashboard.paymentDetails.title')} - {reportDetails?.date}
            </Box>
            <IconButton
              onClick={() => setReportDetailsDialog(false)}
              sx={{ 
                color: '#316C6E',
                '&:hover': {
                  bgcolor: 'rgba(49, 108, 110, 0.04)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ mt: 2, pb: 2, mb: 2, position: 'relative' }}>
            {reportDetails && (
              <>
                <TableContainer component={Paper} sx={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)', mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell>{t('pages.tours.operator.dashboard.paymentDetails.customer')}</TableCell>
                        <TableCell>{t('pages.tours.operator.dashboard.paymentDetails.tour')}</TableCell>
                        <TableCell>{t('pages.tours.operator.dashboard.paymentDetails.tourDate')}</TableCell>
                        <TableCell>{t('pages.tours.operator.dashboard.paymentDetails.bookingDate')}</TableCell>
                        <TableCell>{t('pages.tours.operator.dashboard.paymentDetails.amount')} (DOP)</TableCell>
                        <TableCell>{t('pages.tours.operator.dashboard.paymentDetails.status')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportDetails.transactions.map((transaction) => (
                        <TableRow 
                          key={transaction.id}
                          sx={{ '&:hover': { bgcolor: '#f8f8f8' } }}
                        >
                          <TableCell>{transaction.customerName}</TableCell>
                          <TableCell>{transaction.tourName}</TableCell>
                          <TableCell>{transaction.tourDate}</TableCell>
                          <TableCell>{transaction.bookingDate}</TableCell>
                          <TableCell sx={{ color: '#316C6E', fontWeight: 500 }}>
                            {formatAmount(transaction.amount)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              icon={transaction.status === 'processed' ? <CheckCircleIcon /> : <ScheduleIcon />}
                              label={transaction.status === 'processed' ? 
                                t('pages.tours.operator.dashboard.paymentTable.processed') : 
                                t('pages.tours.operator.dashboard.paymentTable.pending')}
                              color={transaction.status === 'processed' ? 'success' : 'warning'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Paper 
                  sx={{ 
                    position: 'sticky',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 1.5,
                    bgcolor: '#f8f8f8',
                    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                    mt: 'auto'
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          {t('pages.tours.operator.dashboard.paymentDetails.summary.totalAmount')}
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#316C6E' }}>
                          DOP {formatAmount(reportDetails.summary.totalAmount)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          {t('pages.tours.operator.dashboard.paymentDetails.summary.managementFee')}
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#d32f2f' }}>
                          DOP {formatAmount(reportDetails.summary.managementFee)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          {t('pages.tours.operator.dashboard.paymentDetails.summary.duePayment')}
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#2e7d32' }}>
                          DOP {formatAmount(reportDetails.summary.finalAmount)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default OperatorPaymentHistory; 